import { useCallback } from "react";
import { Controller, type Control, type FieldPathByValue } from "react-hook-form";
import CountryDropdown from "@/components/AddressAutoComplete/CountryDropdown";
import AddressAutocompleteInput from "@/components/AddressAutoComplete/AddressAutocompleteInput";
import PostalCodeInput from "@/components/AddressAutoComplete/PostalCodeInput";
import StateDropdown from "@/components/AddressAutoComplete/StateInput";
import E164PhoneNumberInput from "@/components/E164PhoneNumberInput/E164PhoneNumberInput";
import { ControlledTextField as TextField } from "@/components/TextField";
import useAddressAutocomplete from "@/hooks/useAddressAutocompelte";
import {
  requiredFieldRules,
  validateBeneficiaryPhone,
  type BeneficiariesFormValues,
  type BeneficiaryGroup
} from "./beneficiaryFormUtils";

type BeneficiaryContactFormFieldsProps = {
  control: Control<BeneficiariesFormValues>;
  group: BeneficiaryGroup;
  index: number;
  disabled: boolean;
};

type ContactFieldKey =
  | "address1"
  | "address2"
  | "city"
  | "state"
  | "postalCode"
  | "countryCode"
  | "phone"
  | "email"
  | "isVerified";

export default function BeneficiaryContactFormFields({
  control,
  group,
  index,
  disabled
}: BeneficiaryContactFormFieldsProps) {
  const name = useCallback(
    (field: ContactFieldKey) => `${group}.${index}.${field}` as FieldPathByValue<BeneficiariesFormValues, string>,
    [group, index]
  );

  const { isUSA, ...autocompleteProps } = useAddressAutocomplete({
    mapName: (key) => {
      if (key === "address1") return name("address1");
      if (key === "isVerified") return name("isVerified");
      return name(key as Exclude<ContactFieldKey, "phone" | "email">);
    }
  });

  return (
    <div className="flex flex-col gap-6" data-testid={`${group}-contact-${index}`}>
      <CountryDropdown
        control={control}
        name={name("countryCode")}
        required
        rules={requiredFieldRules("Country is required")}
      />

      <AddressAutocompleteInput
        control={control}
        name={name("address1")}
        required
        label="Mailing Street Address"
        rules={requiredFieldRules("Mailing street address is required")}
        {...autocompleteProps}
      />

      <TextField
        control={control}
        name={name("address2")}
        className="bg-sys-brand-surface"
        label="Street Address Line 2"
        disabled={disabled}
      />

      <div className="flex flex-col gap-6 sm:flex-row">
        <TextField
          control={control}
          name={name("city")}
          className="bg-sys-brand-surface flex-[5]"
          label="City"
          required
          disabled={disabled}
          rules={requiredFieldRules("City is required")}
        />
        <div className="flex-[1]">
          <StateDropdown
            control={control}
            name={name("state")}
            isUSA={isUSA}
            required={isUSA}
            disabled={disabled}
            rules={isUSA ? requiredFieldRules("State is required") : undefined}
          />
        </div>
        <PostalCodeInput
          control={control}
          name={name("postalCode")}
          isUSA={isUSA}
          className="flex-[1]"
          required
          disabled={disabled}
          rules={requiredFieldRules(isUSA ? "Zip is required" : "Postal code is required")}
        />
      </div>

      <Controller
        name={name("phone")}
        control={control}
        rules={{ validate: validateBeneficiaryPhone }}
        render={({ field, fieldState }) => (
          <E164PhoneNumberInput
            label="Phone (optional)"
            value={field.value ?? ""}
            onChange={field.onChange}
            error={!!fieldState.error}
            errorText={fieldState.error?.message}
            disabled={disabled}
          />
        )}
      />

      <TextField
        control={control}
        name={name("email")}
        className="bg-sys-brand-surface"
        label="Email (optional)"
        type="email"
        disabled={disabled}
        rules={{
          validate: (value) => {
            if (!value?.trim()) return true;
            return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim()) || "Enter a valid email address";
          }
        }}
      />
    </div>
  );
}
